import { useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { CurrencyType } from "../../../../../enums";

interface IUseCurrencyDropdownProps {
  currency?: CurrencyType | CurrencyType[];
  disabled?: boolean;
  currencyName?: string;
  setValue?: (name: string, value: any) => void;
}

export const useCurrencyDropdown = ({
  currency,
  disabled,
  currencyName,
  setValue,
}: IUseCurrencyDropdownProps) => {
  const { watch } = useFormContext();
  const formCurrencyValue = currencyName ? watch(currencyName) : undefined;

  const currencies = Array.isArray(currency)
    ? currency
    : currency !== undefined
    ? [currency]
    : [];

  // Initialize with form value if available, otherwise use first currency in array
  const getInitialCurrency = () => {
    if (formCurrencyValue && currencies.includes(formCurrencyValue)) {
      return formCurrencyValue;
    }
    return currencies.length > 0 ? currencies[0] : undefined;
  };

  const [selectedCurrency, setSelectedCurrency] = useState<
    CurrencyType | undefined
  >(getInitialCurrency());

  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);
  const currencyDropdownRef = useRef<HTMLDivElement>(null);
  const isMultiCurrency = Array.isArray(currency) && currency.length > 1;

  // Sync selected currency with form value
  useEffect(() => {
    if (formCurrencyValue && currencies.includes(formCurrencyValue)) {
      setSelectedCurrency(formCurrencyValue);
    }
  }, [formCurrencyValue, currencies]);

  // Click outside to close currency dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        currencyDropdownRef.current &&
        !currencyDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCurrencyDropdown(false);
      }
    };

    if (showCurrencyDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showCurrencyDropdown]);

  const handleCurrencyClick = () => {
    if (isMultiCurrency && !disabled) {
      setShowCurrencyDropdown(!showCurrencyDropdown);
    }
  };

  const handleCurrencySelect = (curr: CurrencyType) => {
    setSelectedCurrency(curr);
    setShowCurrencyDropdown(false);
    if (currencyName && setValue) {
      setValue(currencyName, curr);
    }
  };

  return {
    currencies,
    selectedCurrency,
    showCurrencyDropdown,
    currencyDropdownRef,
    isMultiCurrency,
    handleCurrencyClick,
    handleCurrencySelect,
  };
};
